import { LogOut } from 'lucide-react';
import { SessionClock } from '../../features/quiz/SessionClock';

export interface QuestionHeaderProps {
  examLabel: string;
  sectionName: string;
  mode: 'practice' | 'exam';
  current: number;
  total: number;
  modeLabel?: string;
  progressPercent?: number;
  answeredLabel?: string;
  /** Mock exam countdown only — never pass for practice. */
  remainingTime?: number;
  /** Mock total duration (ms) for remaining ring. */
  durationMs?: number | null;
  /** @deprecated Urgency is derived from remainingTime inside SessionClock. */
  isTimeLow?: boolean;
  /** Practice elapsed (count-up) — not a deadline. */
  elapsedMs?: number;
  /** Training only: freeze / clear the elapsed clock. Never for Mock. */
  clockPaused?: boolean;
  onToggleClockPause?: () => void;
  onResetClock?: () => void;
  onExit?: () => void;
}

export function QuestionHeader({
  examLabel,
  sectionName,
  mode,
  current,
  total,
  modeLabel,
  progressPercent,
  answeredLabel,
  remainingTime,
  durationMs = null,
  elapsedMs,
  clockPaused = false,
  onToggleClockPause,
  onResetClock,
  onExit,
}: QuestionHeaderProps) {
  const percent = progressPercent ?? Math.round((current / Math.max(total, 1)) * 100);
  const displayMode = modeLabel ?? (mode === 'exam' ? '模拟考试' : '专项训练');
  const questionCounter =
    mode === 'exam' ? `第 ${current} / ${total} 题` : `第 ${current} 题`;

  return (
    <header className="sticky top-0 z-30 flex-shrink-0 border-b border-slate-200 bg-white/95 backdrop-blur">
      <div className="mx-auto flex max-w-7xl flex-col gap-4 px-4 py-4 sm:px-6 lg:px-8">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex min-w-0 items-center gap-3">
            {onExit && (
              <button
                type="button"
                onClick={onExit}
                className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-md border border-slate-200 bg-white text-slate-500 shadow-sm transition-colors hover:bg-slate-50 hover:text-slate-800 focus:outline-none focus:ring-2 focus:ring-slate-400 focus:ring-offset-2"
                aria-label={mode === 'exam' ? '退出模拟考试' : '结束本轮训练'}
              >
                <LogOut className="h-4 w-4" />
              </button>
            )}
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <span className="rounded-full bg-slate-950 px-2.5 py-1 text-xs font-semibold text-white">
                  {displayMode}
                </span>
                <span className="text-sm font-semibold text-slate-950">{examLabel}</span>
                <span className="text-sm text-slate-400">/</span>
                <span className="truncate text-sm font-medium text-slate-600">{sectionName}</span>
              </div>
              <p className="mt-1 text-xs text-slate-500">
                {answeredLabel ?? `已答 ${Math.max(0, current - 1)} 题`}
              </p>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            {mode === 'exam' && remainingTime != null && (
              <SessionClock
                variant="countdown"
                ms={remainingTime}
                totalMs={durationMs}
              />
            )}
            {mode === 'practice' && elapsedMs != null && (
              <SessionClock
                variant="elapsed"
                ms={elapsedMs}
                paused={clockPaused}
                onTogglePause={onToggleClockPause}
                onReset={onResetClock}
              />
            )}
            <span className="rounded-full bg-primary-50 px-3 py-1.5 text-sm font-semibold text-primary-700 ring-1 ring-primary-100">
              {questionCounter}
            </span>
          </div>
        </div>

        <div className="h-2 overflow-hidden rounded-full bg-slate-100">
          <div
            className="h-full rounded-full bg-primary-600 transition-all duration-300"
            style={{ width: `${Math.min(100, Math.max(0, percent))}%` }}
          />
        </div>
      </div>
    </header>
  );
}
